/**
 * ClientKey (2026-10-21) — the app's name for ONE create, so a create whose
 * answer was lost on the way back (phone dropped off the network, a 503 the
 * browser re-sent) is never made twice. Shared by the app and the server
 * (api/src/writeRun.ts): the server looks a keyed create up first and, when
 * the caller already made it, answers with the existing row instead of
 * creating another. Design: docs/offline-sync.md "Duplicate creates — fixed
 * 2026-10-21". No browser or Graph imports (the server compiles this file).
 */

/** Letters, digits, "-" and "_", 8–80 characters (a UUID with a short prefix fits). */
export const CLIENT_KEY_PATTERN = /^[A-Za-z0-9_-]{8,80}$/;

/** Lists whose creates carry a ClientKey column (provisioning indexes it on each). */
export const KEYED_LISTS: ReadonlySet<string> = new Set(['Audits', 'AuditNotes', 'Tasks', 'TaskComments']);

export function isClientKey(value: unknown): value is string {
  return typeof value === 'string' && CLIENT_KEY_PATTERN.test(value);
}

/**
 * A key derived from an id the device already keeps (an audit's localId, a
 * queued note's or task draft's id) — the same on every re-sync. undefined
 * when the id can't make a valid key (the create then simply goes unkeyed).
 */
export function stableClientKey(prefix: string, id: string): string | undefined {
  const key = `${prefix}-${id}`;
  return isClientKey(key) ? key : undefined;
}

/** A fresh random key: `prefix-` + 32 hex characters. */
export function newClientKey(prefix: string): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `${prefix}-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The key for one submission of a form (a task comment, a new task, a
 * completion): the SAME key while the person retries the same content after
 * a failure, a new one once it succeeded (reset) or once they change what
 * they're sending — a changed comment is a new comment, never a replay of
 * the one that may have landed.
 */
export class SubmissionKey {
  private key: string | null = null;
  private content: string | null = null;

  constructor(private readonly prefix: string) {}

  /** The key to send with `content` (anything that identifies what is being submitted). */
  for(content: string): string {
    if (this.key === null || this.content !== content) {
      this.key = newClientKey(this.prefix);
      this.content = content;
    }
    return this.key;
  }

  /** After a success: the next submission gets a new key. */
  reset(): void {
    this.key = null;
    this.content = null;
  }
}

/** Is this a SharePoint/Graph error about the ClientKey column (not provisioned yet, or not indexed)? */
export function namesClientKey(message: unknown): boolean {
  return typeof message === 'string' && /clientkey/i.test(message);
}
